import { useEffect, useRef } from 'react';

/**
 * pre243 — Mini-Pegelanzeige (Google-Meet-Stil, drei springende Balken).
 * Zweck: Bei Kommunikationsproblemen sofort sehen, ob auf BEIDEN Seiten das
 * Mikrofon einen Pegel liefert (Berater lokal + Gast remote). Rein visuell,
 * keine Aufzeichnung; Analyser liest nur Momentanpegel.
 */

// Ein gemeinsamer AudioContext für alle Meter (Browser-Limit, Ressourcen).
let sharedCtx: AudioContext | null = null;
function audioCtx(): AudioContext | null {
  try {
    if (!sharedCtx) sharedCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (sharedCtx.state === 'suspended') void sharedCtx.resume();
    return sharedCtx;
  } catch { return null; }
}

export function AudioLevelMeter({ stream, title }: { stream: MediaStream | null; title?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const W = 15, H = 18; // pre244: halb so breit
    canvas.width = W * dpr; canvas.height = H * dpr;
    const g = canvas.getContext('2d');
    if (!g) return;
    g.scale(dpr, dpr);

    let raf = 0;
    let analyser: AnalyserNode | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let level = 0; // geglätteter Pegel 0..1
    const hasAudio = !!stream && stream.getAudioTracks().some((t) => t.readyState === 'live');

    if (hasAudio) {
      const ctx = audioCtx();
      if (ctx) {
        try {
          source = ctx.createMediaStreamSource(stream!);
          analyser = ctx.createAnalyser();
          analyser.fftSize = 512;
          source.connect(analyser); // kein connect(destination) — nur Messung, kein Echo
        } catch { analyser = null; }
      }
    }
    const buf = analyser ? new Uint8Array(analyser.fftSize) : null;

    const draw = () => {
      if (analyser && buf) {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        // Sprachpegel auf 0..1 spreizen und glätten (schnell rauf, langsam runter)
        const target = Math.min(1, rms * 10); // pre244: empfindlicher (höherer Ausschlag)
        level = target > level ? target : level * 0.85 + target * 0.15;
      } else {
        level = 0;
      }
      g.clearRect(0, 0, W, H);
      const active = level > 0.06;
      const color = !hasAudio ? '#777' : active ? '#7ee787' : 'rgba(255,255,255,0.55)';
      // Drei Balken: außen folgen dem Pegel gedämpft, Mitte voll (Meet-Optik).
      const heights = [0.6, 1.0, 0.8].map((f, i) => {
        const base = 2;
        const wobble = active ? Math.abs(Math.sin(Date.now() / (90 + i * 40))) : 0;
        return base + (H - base - 2) * Math.min(1, level * f * (0.7 + 0.3 * wobble));
      });
      heights.forEach((h, i) => {
        const bw = 2.5, gap = 2, x = 2.5 + i * (bw + gap), y = (H - h) / 2;
        g.fillStyle = color;
        g.beginPath();
        (g as any).roundRect ? (g as any).roundRect(x, y, bw, h, 2.5) : g.rect(x, y, bw, h);
        g.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      try { source?.disconnect(); } catch { /* */ }
    };
  }, [stream]);

  return (
    <canvas
      ref={canvasRef}
      title={title ?? 'Mikrofon-Pegel'}
      style={{ width: 15, height: 18, display: 'block', background: 'transparent', borderRadius: 6 }}
    />
  );
}
